import { SkatingSession } from '@types';
import { PDFViewerModal } from '_components/atoms';
import UserService from '_services/UserService';
import HelperMethods from '_utils/HelperMethods';
import React, { useEffect, useState } from 'react';
import Barcode from 'react-barcode';

const MyBookedSkatingSessions: React.FC = () => {
  const [sessions, setSessions] = useState<SkatingSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [pdfViewerModal, setPdfViewerModal] = useState<{
    isOpen: boolean;
    url: string;
    title: string;
  }>({
    isOpen: false,
    url: '',
    title: '',
  });

  const fetchSessions = async () => {
    try {
      setLoading(true);

      const responseData =
        (await UserService.getBookedSkatingSessions()) as unknown as SkatingSession[];

      if (responseData) {
        setSessions(responseData);
      }
    } catch (error) {
      console.error('Error fetching sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSessions();
  }, []);

  const openPdfViewer = (url: string, title: string) => {
    setPdfViewerModal({
      isOpen: true,
      url,
      title,
    });
  };

  const closePdfViewer = () => {
    setPdfViewerModal({
      isOpen: false,
      url: '',
      title: '',
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            My Booked Skating Sessions
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            View your booked skating sessions
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <div className="text-sm text-gray-600">
            Total Sessions:{' '}
            <span className="font-semibold">{sessions.length}</span>
          </div>
        </div>
      </div>

      {/* Sessions Table - Desktop */}
      <div className="hidden md:block bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"></th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Session Info
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>

                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider text-center">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sessions.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    className="px-6 py-12 text-center text-gray-500"
                  >
                    {loading ? 'Loading...' : 'No sessions found'}
                  </td>
                </tr>
              ) : (
                sessions.map(session => (
                  <tr key={session.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col items-center">
                        <div className="mb-2">
                          <Barcode
                            value={session.barcodeId}
                            width={1}
                            height={50}
                            fontSize={10}
                            format="CODE128"
                          />
                        </div>
                        <div className="text-xs text-gray-500 text-center">
                          {session.barcodeId}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        Session #{session.skatingSessionId}
                      </div>
                      <div className="text-sm text-gray-500">
                        {HelperMethods.formatDate(session.sessionDate)}
                      </div>
                      <div className="text-sm text-gray-500">
                        {HelperMethods.formatTime12Hour(session.timeSlot)}
                      </div>
                      <div className="text-xs text-gray-400">
                        Receipt: {session.receiptNumber}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {session.customerName}
                      </div>
                      <div className="text-sm text-gray-500">
                        {session.customerEmail}
                      </div>
                      <div className="text-sm text-gray-500">
                        {session.customerPhone}
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          session.redeemed
                            ? 'bg-green-100 text-green-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}
                      >
                        {session.redeemed ? 'Redeemed' : 'Not Redeemed'}
                      </span>
                      {session.redeemed && session.redeemedBy && (
                        <div className="text-xs text-gray-500 mt-1">
                          by {session.redeemedBy}
                        </div>
                      )}
                      {session.redeemed && session.redeemedAt && (
                        <div className="text-xs text-gray-500 mt-1">
                          Redeemed on{' '}
                          {HelperMethods.formatDateTimeLocal(
                            session.redeemedAt,
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2 justify-between">
                        {session.waiverUrl ? (
                          <button
                            onClick={() =>
                              openPdfViewer(session.waiverUrl!, 'View Waiver')
                            }
                            className="text-blue-600 hover:text-blue-900"
                          >
                            View Waiver
                          </button>
                        ) : (
                          <div className="text-gray-500">No Waiver</div>
                        )}
                        {session.receiptUrl && (
                          <button
                            onClick={() =>
                              openPdfViewer(session.receiptUrl!, 'View Receipt')
                            }
                            className="text-blue-600 hover:text-blue-900"
                          >
                            View Receipt
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Sessions Cards - Mobile */}
      <div className="md:hidden space-y-4">
        {sessions.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center text-gray-500">
            {loading ? 'Loading...' : 'No sessions found'}
          </div>
        ) : (
          sessions.map(session => (
            <div
              key={session.id}
              className="bg-white rounded-lg shadow-sm border border-gray-200 p-4"
            >
              {/* Session Info */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Session #{session.skatingSessionId}
                  </h3>
                  <span
                    className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      session.redeemed
                        ? 'bg-green-100 text-green-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}
                  >
                    {session.redeemed ? 'Redeemed' : 'Not Redeemed'}
                  </span>
                </div>
                <div className="text-sm text-gray-600">
                  {HelperMethods.formatDate(session.sessionDate)}
                </div>
                <div className="text-sm text-gray-600">
                  {HelperMethods.formatTime12Hour(session.timeSlot)}
                </div>
                <div className="text-xs text-gray-400 mt-1">
                  Receipt: {session.receiptNumber}
                </div>
              </div>

              {/* Customer Info */}
              <div className="mb-4 p-3 bg-gray-50 rounded-md">
                <h4 className="text-sm font-medium text-gray-900 mb-2">
                  Customer
                </h4>
                <div className="text-sm text-gray-600">
                  <div className="font-medium">{session.customerName}</div>
                  <div>{session.customerEmail}</div>
                  <div>{session.customerPhone}</div>
                </div>
              </div>

              {/* Barcode Info */}
              <div className="mb-4 p-3 bg-gray-50 rounded-md">
                <div className="flex flex-col items-center">
                  <div className="mb-2">
                    <Barcode
                      value={session.barcodeId}
                      width={1}
                      height={60}
                      fontSize={12}
                      format="CODE128"
                    />
                  </div>
                  <div className="text-xs text-gray-500 text-center">
                    {session.barcodeId}
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-2">
                {session.waiverUrl && (
                  <button
                    onClick={() =>
                      openPdfViewer(session.waiverUrl!, 'View Waiver')
                    }
                    className="inline-flex items-center px-3 py-2 bg-blue-100 text-blue-700 text-sm rounded-md hover:bg-blue-200 transition-colors"
                  >
                    View Waiver
                  </button>
                )}
                {session.receiptUrl && (
                  <button
                    onClick={() =>
                      openPdfViewer(session.receiptUrl!, 'View Receipt')
                    }
                    className="inline-flex items-center px-3 py-2 bg-blue-100 text-blue-700 text-sm rounded-md hover:bg-blue-200 transition-colors"
                  >
                    View Receipt
                  </button>
                )}
              </div>

              {/* Redeemed By Info */}
              {session.redeemed && session.redeemedBy && (
                <div className="mt-3 text-xs text-gray-500">
                  Redeemed by: {session.redeemedBy}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* PDF Viewer Modal */}
      <PDFViewerModal
        isOpen={pdfViewerModal.isOpen}
        onClose={closePdfViewer}
        url={pdfViewerModal.url}
        title={pdfViewerModal.title}
      />
    </div>
  );
};

export default MyBookedSkatingSessions;
